import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { selectedStatesAlpha } from "../refdata/FilterOptions";

const PREFIX = "ModalDialog";

const classes = {
  root: `${PREFIX}-root`,
  dialogActionsTop: `${PREFIX}-dialogActionsTop`,
  dialogActions: `${PREFIX}-dialogActions`,
  closeButton: `${PREFIX}-closeButton`,
  selectedLocationStyle: `${PREFIX}-selectedLocationStyle`,
  divider: `${PREFIX}-divider`,
  modalContent: `${PREFIX}-modalContent`,
  modalContentOSCR: `${PREFIX}-modalContentOSCR`,
  cancelButton: `${PREFIX}-cancelButton`,
  saveButton: `${PREFIX}-saveButton`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`&.${classes.root}`]: {
    "& #modal-title-box": {
      paddingBottom: "0px",
      marginBottom: "-15px",
    },
    "& .MuiDialogTitle-root": {
      "& h2": {
        fontSize: 16,
        paddingBottom: 0,
      },
      "& #modal-title": {
        paddingBottom: 2,
      },
      "& #modal-content > *": {
        padding: 0,
      },
    },
  },

  [`& .${classes.dialogActionsTop}`]: {
    "& button.MuiButton-containedSecondary": {
      borderRadius: 3,
      width: 200,
      fontSize: "1rem",
      marginRight: "1rem",
    },
    "& button.MuiButton-contained.Mui-disabled": {
      backgroundColor: "#e0e0e0",
    },
  },

  [`& .${classes.dialogActions}`]: {
    "marginTop": "1rem",
    "backgroundColor": theme.palette.primary.main,
    "& button.MuiButton-containedSecondary": {
      borderRadius: 3,
      width: 200,
      fontSize: "1rem",
      height: 40,
      margin: "1rem",
    },
    "& button.MuiButton-contained.Mui-disabled": {
      backgroundColor: "#e0e0e0",
    },
  },

  [`& .${classes.closeButton}`]: {
    "position": "absolute",
    "right": theme.spacing(1),
    "top": theme.spacing(1),
    "color": "#acc8df",
    "padding": 0,
    "marginRight": 16,
    "backgroundColor": "transparent",
    "& svg": {
      fontSize: "2rem",
    },
    "&:focus": {
      background: "rgba(0, 0, 0, 0.04)",
    },
  },

  [`& .${classes.selectedLocationStyle}`]: {
    fontSize: 18,
    color: theme.palette.primary.main,
    fontWeight: "bold",
    padding: 1,
  },

  [`& .${classes.divider}`]: {
    "&.MuiDivider-root": {
      margin: "15px auto 15px auto",
      borderColor: "#e4eef7",
      padding: "0px",
    },
  },

  [`& .${classes.modalContent}`]: {
    "&#modal-content": {
      overflowX: "hidden",
      padding: "24px 24px 8px 24px",
    },
  },
  [`& .${classes.modalContentOSCR}`]: {
    "&#modal-content": {
      overflowX: "hidden",
      padding: "0px 0px 8px 0px",
    },
  },

  [`& .${classes.cancelButton}`]: {
    "backgroundColor": "white",
    "&:hover, &.Mui-focusVisible": {
      "color": "#ffffff",
      "&#closeModal": { backgroundColor: theme.palette.primary.main },
    },
  },

  [`& .${classes.saveButton}`]: {
    "&:hover, &.Mui-focusVisible": {
      "color": "#ffffff",
      "&#submitModal": { backgroundColor: theme.palette.primary.main },
    },
  },
}));

interface Props {
  children: React.ReactNode;
  title: string;
  display: boolean;
  setDisplay: (display: boolean) => void;
  subtitle?: React.ReactNode;
  onExited?: () => void;
  maxWidth?: DialogProps["maxWidth"];
  id?: string;
  onAction?: () => void;
  onSubmit?: () => void;
  submitText?: string;
  disabledSubmit?: boolean;
  partialMergerModal?: any;
  noCloseXIcon?: boolean;
  selectedLocations?: any;
  moveActionButtons?: boolean;
  overwrite?: string;
  handleClick?: () => void;
  onClose?: () => void;
}

export default function ModalDialog({
  title,
  subtitle = <></>,
  children,
  display,
  setDisplay,
  onExited,
  maxWidth,
  id,
  onAction,
  onSubmit,
  submitText,
  disabledSubmit,
  partialMergerModal,
  noCloseXIcon,
  selectedLocations,
  moveActionButtons,
  overwrite,
  handleClick,
  onClose,
}: Props) {
  const handleClose = () => {
    setDisplay(false);
    onClose?.();
  };
  const dialogActions = (
    <DialogActions
      className={
        moveActionButtons ? classes.dialogActionsTop : classes.dialogActions
      }
    >
      <div>
        <Button
          className={moveActionButtons ? classes.cancelButton : ""}
          variant="contained"
          color="secondary"
          onClick={handleClick || handleClose}
          id="closeModal"
          style={
            moveActionButtons ? { marginBottom: "0", marginRight: "16px" } : {}
          }
        >
          {overwrite || "Cancel"}
        </Button>
        <Button
          className={moveActionButtons ? classes.saveButton : ""}
          disabled={disabledSubmit}
          variant="contained"
          color="secondary"
          type={onSubmit ? "submit" : "button"}
          id="submitModal"
          onClick={onAction}
          style={moveActionButtons ? { marginBottom: "0" } : {}}
        >
          {submitText || "Done"}
        </Button>
      </div>
    </DialogActions>
  );

  const dialogBody = (
    <>
      <DialogTitle id="modal-title-box">
        <Grid container>
          <Grid style={{ textAlign: "left" }} size={6}>
            <Typography
              id="modal-title"
              color="primary.main"
              variant="h5"
              component="div"
            >
              <b>{title}</b>
            </Typography>
          </Grid>
          <Grid style={{ textAlign: "left" }} size={6}>
            {selectedLocations ? (
              <>
                <Typography
                  id="selected-locations"
                  className={classes.selectedLocationStyle}
                >
                  Selected Locations{" "}
                </Typography>
                <Typography
                  component="span"
                  style={{
                    color: "black",
                    fontWeight: "normal",
                    fontSize: "15px",
                    display: "inline-block",
                    whiteSpace: "normal",
                    width: "100%",
                    wordBreak: "break-word",
                  }}
                >
                  {selectedLocations.flat().length ===
                    selectedStatesAlpha.flat().length ||
                  selectedLocations.flat().length === 0
                    ? "All States and Territories"
                    : selectedLocations.join(",")}
                </Typography>
              </>
            ) : (
              ""
            )}
          </Grid>
          {subtitle ? (
            <Grid container>
              <Grid
                size={{
                  sm: moveActionButtons ? 4 : 12,
                }}
              >
                <Typography
                  id="modal-subtitle"
                  color="primary.main"
                  component={"div"}
                  style={
                    moveActionButtons ? { fontSize: 16, paddingTop: 14 } : {}
                  }
                >
                  {subtitle}
                </Typography>
              </Grid>
              <Grid
                size={{
                  sm: 8,
                }}
              >
                {moveActionButtons && dialogActions}
              </Grid>
            </Grid>
          ) : (
            ""
          )}
        </Grid>

        {!noCloseXIcon && (
          <IconButton
            id="close-modal-button"
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
            size="large"
          >
            {!partialMergerModal && <CloseIcon />}
          </IconButton>
        )}
        <Divider className={classes.divider} />
      </DialogTitle>
      <DialogContent
        id="modal-content"
        className={
          id === "oscr" ? classes.modalContentOSCR : classes.modalContent
        }
        style={partialMergerModal ? { paddingBottom: 0 } : {}}
      >
        {children}
      </DialogContent>
    </>
  );

  return (
    <StyledDialog
      id={id || "modal"}
      open={display}
      disableAutoFocus={false}
      onClose={(event: any, reason: string) => {
        if (reason === "backdropClick" && !noCloseXIcon) {
          handleClose();
        }
      }}
      maxWidth={maxWidth || "md"}
      fullWidth
      className={classes.root}
      aria-describedby="modal-title"
      aria-labelledby="modal-title"
      slotProps={{ transition: { onExited: onExited } }}
    >
      {onSubmit ? (
        <form
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            onSubmit();
          }}
          id="modal-dialog"
        >
          {dialogBody}
          {!moveActionButtons && dialogActions}
        </form>
      ) : (
        <>
          {dialogBody}
          {onAction && dialogActions}
        </>
      )}
    </StyledDialog>
  );
}
